/**
 * Serviço de API para Sincronização de Aulas
 *
 * Gerencia as chamadas às APIs de verificação e sincronização de aulas
 * entre o material do facilitador e o material padrão.
 */

const apiUrl = process.env.REACT_APP_API_HOST || 'http://127.0.0.1:5000';

/**
 * Verifica se o material do facilitador precisa ser sincronizado com o padrão
 *
 * @param {number} idTemasmodulo - ID do registro fac_temasmodulo do facilitador
 * @returns {Promise<Object>} Objeto com informações sobre a necessidade de sincronização
 * @throws {Error} Se houver erro na requisição
 */
export const verificarSincronizacao = async (idTemasmodulo) => {
  try {
    const response = await fetch(`${apiUrl}/api/verificarsincronizacao`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id_temasmodulo: idTemasmodulo
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao verificar sincronização');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao verificar sincronização:', error);
    throw error;
  }
};

/**
 * Executa a sincronização do material do facilitador com o padrão
 *
 * @param {number} idTemasmodulo - ID do registro fac_temasmodulo do facilitador
 * @returns {Promise<Object>} Objeto com o resultado da sincronização
 * @throws {Error} Se houver erro na requisição
 */
export const sincronizarAulas = async (idTemasmodulo) => {
  try {
    const response = await fetch(`${apiUrl}/api/sincronizaraulasfacilitador`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id_temasmodulo: idTemasmodulo
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erro ao sincronizar aulas');
    }

    return await response.json();
  } catch (error) {
    console.error('Erro ao sincronizar aulas:', error);
    throw error;
  }
};

/**
 * Gera uma mensagem amigável sobre a necessidade de sincronização
 *
 * @param {Object} verificacao - Objeto retornado pela verificação
 * @returns {string} Mensagem amigável para o usuário
 */
export const gerarMensagemSincronizacao = (verificacao) => {
  if (!verificacao.precisa_sincronizar) {
    return 'Seu material está sincronizado com o padrão! 😊';
  }

  const { diferenca, tipo_sincronizacao, qtd_apresentacoes_faltantes } = verificacao;
  const aulasPalavra = Math.abs(diferenca) === 1 ? 'aula' : 'aulas';

  if (tipo_sincronizacao === 'adicionar') {
    return `O material padrão foi atualizado e possui ${Math.abs(diferenca)} ${aulasPalavra} a mais que o seu material. ` +
           `Existem ${qtd_apresentacoes_faltantes} ${qtd_apresentacoes_faltantes === 1 ? 'apresentação nova' : 'novas apresentações'} ` +
           `para você alinhar com a agenda da Harve! 📚`;
  } else {
    return `O material padrão foi otimizado e possui ${Math.abs(diferenca)} ${aulasPalavra} a menos. ` +
           `Vamos ajustar seu material para ficar alinhado com a agenda da Harve! 📝`;
  }
};

/**
 * Gera uma mensagem amigável sobre o resultado da sincronização
 *
 * @param {Object} resultado - Objeto retornado pela sincronização
 * @returns {string} Mensagem amigável para o usuário
 */
export const gerarMensagemResultado = (resultado) => {
  if (resultado.message === 'Quantidade de aulas já está sincronizada') {
    return 'Tudo certo! Seu material já está sincronizado! ✨';
  }

  const { diferenca, apresentacoes_faltantes } = resultado;

  if (diferenca > 0) {
    const aulasPalavra = diferenca === 1 ? 'aula foi adicionada' : 'aulas foram adicionadas';
    const apresentacoesPalavra = apresentacoes_faltantes.length === 1 ? 'apresentação nova' : 'novas apresentações';

    return `Perfeito! ${diferenca} ${aulasPalavra} ao seu material! 🎉\n\n` +
           `Foram incluídas ${apresentacoes_faltantes.length} ${apresentacoesPalavra}. ` +
           `Agora você pode organizá-las da forma que preferir para melhor atender sua turma!`;
  } else if (diferenca < 0) {
    const aulasPalavra = Math.abs(diferenca) === 1 ? 'aula foi' : 'aulas foram';

    return `Material otimizado! ${Math.abs(diferenca)} ${aulasPalavra} consolidadas! ✅\n\n` +
           `As apresentações dessas aulas foram movidas para a penúltima aula. ` +
           `Sinta-se à vontade para reorganizá-las da melhor forma para sua turma!`;
  }

  return resultado.message || 'Sincronização realizada com sucesso! ✨';
};
